import React, { createContext, type FC, useEffect, useRef, useState } from 'react';

interface ScreenSizeProviderProps {}

interface ScreenSizeContextProps {
    width: number,
    height: number,
    isLandscape: boolean,
    isMobile: boolean
}

const ScreenSizeContext = createContext<ScreenSizeContextProps>({
    width: 0,
    height: 0,
    isLandscape: true,
    isMobile: false
});

ScreenSizeContext.displayName = "ScreenSizeContext";

const useComponentSize = () => {
  const ref: React.LegacyRef<HTMLDivElement> = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    
    const element = ref.current;
    if (!element) return;

    const observer = new ResizeObserver(entries => {
      // We can assume only one entry for our observed element
      const { width, height } = entries[0].contentRect;
      setSize({ width, height });
    });

    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, []);

  return [ref, size] as const;
};

const ScreenSizeProvider: FC<ScreenSizeProviderProps> = (props) => {
    const [sizeRef, size] = useComponentSize();
    const isLandscape = (width: number, height: number) => {
        return width >= height;
    }

    const isMobile = (width: number, height: number) => {
        return isLandscape(width, height)
            ? height <= 640
            : width <= 640
    }
    
    return (
        <>
            <div ref={sizeRef} style={{width: '100vw', height: '100vh', position:'fixed', left: 0, top: 0, zIndex: -1, pointerEvents: 'none'}} />
            <ScreenSizeContext.Provider
                value={{
                    width: size.width ?? 0,
                    height: size.height?? 0,
                    isLandscape: isLandscape(size.width ?? 0, size.height ?? 0),
                    isMobile: isMobile(size.width ?? 0, size.height ?? 0)
                }}
                {...props}
            />
        </>
    );
};

const useScreenSize = () => React.useContext(ScreenSizeContext);

export { ScreenSizeProvider, useScreenSize };